import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { useLocalisation } from '@archipro-website/localisation';
import {
    createMetaDescription,
    createMetaTitle,
    createOGTags,
    getCanonicalHreflang,
    mergeMeta,
} from '~/modules/seo';
import PolicyPageLayout from '~/modules/root/component/policy-page-layout/PolicyPageLayout';
import { MEMBER_PRIVACY_OPT_OUT_LINK } from '~/modules/privacy/opt-out-section-anchor';
import { SaleOrSharingOptOutForm } from '~/modules/privacy/SaleOrSharingOptOutForm';
import { useUserLoggedIn } from '~/modules/user/hook/use-user';
import { getPrivacyContactEmail } from '~/utils/privacy-contact';

export const loader = async () => {
    return {};
};

export const meta = mergeMeta<typeof loader>(
    () => [],
    (props) => {
        const title = createMetaTitle(props, ['Your Privacy Choices']);
        const description = createMetaDescription([
            'Opt out of the sale or sharing of your personal information, limit the use of sensitive personal information, and see how ArchiPro honours Global Privacy Control signals.',
        ]);

        return [
            { title },
            { name: 'description', content: description },
            ...createOGTags({ meta: props, title, description }),
            ...getCanonicalHreflang(props),
        ];
    },
);

const linkClassName = 'text-blue underline hover:no-underline';

const Section = ({
    title,
    children,
}: {
    title: string;
    children: ReactNode;
}) => (
    <section className="space-y-3">
        <h2 className="text-[1.75rem] leading-tight font-medium text-gray-900 md:text-[2.75rem]">
            {title}
        </h2>
        <div className="space-y-2 text-base leading-[1.53] font-normal text-gray-900 md:text-[1.0625rem]">
            {children}
        </div>
    </section>
);

const YourPrivacyChoices = () => {
    const { country } = useLocalisation();
    const { isLoggedIn } = useUserLoggedIn();
    const privacyEmail = getPrivacyContactEmail(country);

    return (
        <PolicyPageLayout title="Your Privacy Choices">
            <div className="space-y-10 antialiased">
                <p className="text-base leading-[1.53] font-normal text-gray-900 md:text-[1.0625rem]">
                    This page lets US residents exercise their choices about how
                    ArchiPro uses their personal information. For full detail,
                    see our{' '}
                    <a href="/privacy-policy" className={linkClassName}>
                        Privacy Policy
                    </a>
                    .
                </p>

                <Section title="Do not sell or share my personal information">
                    <p>
                        Some of our sharing of personal information with
                        advertising and analytics partners may be a
                        &ldquo;sale&rdquo; or &ldquo;sharing&rdquo; under US
                        state privacy laws. You can opt out of that sharing for
                        targeted advertising. We also honour Global Privacy
                        Control (GPC) signals automatically, so if your browser
                        sends a GPC signal we treat it as an opt-out for that
                        browser or device.
                    </p>
                </Section>

                <Section title="Limit the use of my sensitive personal information">
                    <p>
                        Where we use or disclose sensitive personal information
                        beyond the purposes permitted by law, you can direct us
                        to limit that use to what is necessary to provide the
                        services you have asked for.
                    </p>
                </Section>

                <Section title="How to exercise your choices">
                    <ul className="list-disc space-y-1 pl-5">
                        {isLoggedIn ? (
                            <li>
                                You are signed in, so you can manage your
                                choices in your{' '}
                                <Link
                                    to={MEMBER_PRIVACY_OPT_OUT_LINK}
                                    className={linkClassName}
                                >
                                    account settings under Privacy
                                </Link>
                                .
                            </li>
                        ) : (
                            <li>
                                If you are not signed in, use the form below.
                                You do not need an account. If you have one you
                                can also{' '}
                                <a href="/login" className={linkClassName}>
                                    sign in
                                </a>{' '}
                                and manage your choices under Privacy.
                            </li>
                        )}
                        <li>
                            Global Privacy Control: we honour GPC automatically,
                            so no action is needed if your browser sends it.
                        </li>
                        <li>
                            You can also contact us at{' '}
                            <a
                                href={`mailto:${privacyEmail}`}
                                className={linkClassName}
                            >
                                {privacyEmail}
                            </a>
                            .
                        </li>
                    </ul>
                    <p>
                        We will not discriminate against you for exercising
                        these choices.
                    </p>
                </Section>

                {!isLoggedIn && (
                    <Section title="Opt out by email">
                        <p>
                            Enter your email address and submit. We will stop
                            selling or sharing personal information for this
                            browser straight away, and apply your choice to any
                            ArchiPro account that uses that address. You do not
                            need an account and we will not ask you to verify
                            your identity.
                        </p>
                        <SaleOrSharingOptOutForm />
                    </Section>
                )}
            </div>
        </PolicyPageLayout>
    );
};

export default YourPrivacyChoices;
